import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';

import type { CheckoutStep } from '@t/checkout.types';

interface PlaceOrderSectionProps {
  activeStep: CheckoutStep;
  termsAccepted: boolean;
  isEditingPreviousStep: boolean;
  isSubmitting: boolean;
  isNavigating: boolean;
  orderError: boolean;
  onPlaceOrder: () => void;
  onOpenLeadCapture: () => void;
}

const getTooltipTitle = (activeStep: CheckoutStep, termsAccepted: boolean): string => {
  if (activeStep !== 'review') return 'Complete all steps to place the order.';
  if (!termsAccepted) return 'Accept the terms and conditions to place the order.';
  return '';
};

export const PlaceOrderSection = ({
  activeStep,
  termsAccepted,
  isEditingPreviousStep,
  isSubmitting,
  isNavigating,
  orderError,
  onPlaceOrder,
  onOpenLeadCapture,
}: PlaceOrderSectionProps) => {
  const theme = useTheme();

  const showErrorBanner = orderError && activeStep === 'review';
  const isBusy = isSubmitting || isNavigating;
  const isDisabled = activeStep !== 'review' || !termsAccepted || isEditingPreviousStep || isBusy;
  const buttonLabel = isBusy ? 'PLACING ORDER...' : 'PLACE MY ORDER';
  const tooltipTitle = getTooltipTitle(activeStep, termsAccepted);

  return (
    <>
      {showErrorBanner && (
        <Alert variant="checkoutError" sx={{ borderRadius: '10px', mb: '16px' }}>
          <Typography
            component="p"
            variant="heading.subsection"
            sx={{ color: theme.novus.errorRed, lineHeight: '130%', mb: '10px' }}
          >
            Order Could Not Be Submitted
          </Typography>
          <Typography
            component="p"
            variant="body.md"
            sx={{ color: theme.novus.borderBlack, lineHeight: '130%' }}
          >
            Something went wrong while submitting your order. Please try again in a moment. If the issue continues, you
            can submit your information using our lead form and a representative will help complete your order.{' '}
            <Link variant="inlineError" onClick={onOpenLeadCapture}>
              Click here to submit a lead form.
            </Link>
          </Typography>
        </Alert>
      )}
      <Tooltip title={tooltipTitle} placement="top">
        <span>
          <Button variant="contained" disabled={isDisabled} onClick={onPlaceOrder} fullWidth>
            {buttonLabel}
          </Button>
        </span>
      </Tooltip>
    </>
  );
};
